import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { Truck } from "../../types";
import { colors } from "../../utils/colors";

interface AvailabilitySegment {
  startTime: Date;
  endTime: Date;
  status: "in" | "out";
  duration: number;
}

interface AvailabilityChartProps {
  trucks: Truck[];
  registration: string;
}

const AvailabilityChart: React.FC<AvailabilityChartProps> = ({
  trucks,
  registration,
}) => {
  const availabilitySegments = useMemo(() => {
    if (!trucks || trucks.length === 0) return [];

    const segments: AvailabilitySegment[] = [];

    const sortedTrucks = [...trucks].sort(
      (a, b) => new Date(a.intime).getTime() - new Date(b.intime).getTime()
    );

    sortedTrucks.forEach((truck, index) => {
      const intime = new Date(truck.intime);
      const direction = truck.direction?.toLowerCase() || "in";

      let endTime: Date;
      if (index < sortedTrucks.length - 1) {
        endTime = new Date(sortedTrucks[index + 1].intime);
      } else {
        endTime = truck.outtime ? new Date(truck.outtime) : new Date();
      }

      segments.push({
        startTime: intime,
        endTime: endTime,
        status: direction === "out" ? "out" : "in",
        duration: (endTime.getTime() - intime.getTime()) / (1000 * 60 * 60),
      });
    });

    return segments;
  }, [trucks]);

  const totalDuration = useMemo(() => {
    if (availabilitySegments.length === 0) return 0;

    const firstSegment = availabilitySegments[0];
    const lastSegment = availabilitySegments[availabilitySegments.length - 1];

    return (
      (lastSegment.endTime.getTime() - firstSegment.startTime.getTime()) /
      (1000 * 60 * 60)
    );
  }, [availabilitySegments]);

  const formatDateTime = (date: Date) => {
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatDuration = (hours: number) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`;
    }
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
  };

  if (availabilitySegments.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          No availability data for {registration}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ position: "relative", mb: 2 }}>
        <Box
          sx={{
            height: 30,
            backgroundColor: colors.background,
            borderRadius: 4,
            position: "relative",
            overflow: "hidden",
            border: "1px solid #ddd",
            width: "100%",
          }}
        >
          {availabilitySegments.map((segment, index) => {
            const startPercent =
              ((segment.startTime.getTime() -
                availabilitySegments[0].startTime.getTime()) /
                (availabilitySegments[
                  availabilitySegments.length - 1
                ].endTime.getTime() -
                  availabilitySegments[0].startTime.getTime())) *
              100;
            const widthPercent = (segment.duration / totalDuration) * 100;

            return (
              <Tooltip
                key={index}
                title={
                  <Box>
                    <Typography variant="body2">
                      <strong>
                        {segment.status === "in" ? "Unavailable" : "Available"}
                      </strong>
                    </Typography>
                    <Typography variant="body2">
                      From: {formatDateTime(segment.startTime)}
                    </Typography>
                    <Typography variant="body2">
                      To: {formatDateTime(segment.endTime)}
                    </Typography>
                    <Typography variant="body2">
                      Duration: {formatDuration(segment.duration)}
                    </Typography>
                  </Box>
                }
                arrow
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: `${startPercent}%`,
                    width: `${widthPercent}%`,
                    height: "100%",
                    backgroundColor:
                      segment.status === "in" ? colors.error : colors.success,
                    cursor: "pointer",
                    transition: "opacity 0.2s",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: colors.error,
              borderRadius: 2,
            }}
          />
          <Typography variant="body2" sx={{ fontFamily: "SigmaSans" }}>
            Unavailable (In)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: colors.success,
              borderRadius: 2,
            }}
          />
          <Typography variant="body2" sx={{ fontFamily: "SigmaSans" }}>
            Available (Out)
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 2,
          color: "text.secondary",
          fontStyle: "italic",
          fontFamily: "SigmaSans",
        }}
      >
        Hover over segments to see detailed time information
      </Typography>
    </Box>
  );
};

export default AvailabilityChart;
